/**
 * Compact weather display + "Consider the Weather" toggle for the Filters tab.
 * Shows current conditions from Open-Meteo.
 */

import type { WeatherData } from '@/hooks/useWeather';

interface WeatherWidgetProps {
  weather: WeatherData | null;
  loading: boolean;
  considerWeather?: boolean;
  onConsiderWeatherChange?: (value: boolean) => void;
}

export function WeatherWidget({ weather, loading, considerWeather, onConsiderWeatherChange }: WeatherWidgetProps) {
  if (!weather && !loading) return null;

  if (loading) {
    return (
      <div className="p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border)] animate-pulse">
        <div className="h-4 w-32 rounded bg-[var(--text-secondary)]/20" />
      </div>
    );
  }

  if (!weather) return null;

  const feelsLikeDiff = Math.abs(weather.temperature - weather.apparentTemperature) >= 3;
  const windNotable = weather.windSpeed >= 15;

  return (
    <div className="p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border)] space-y-2.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-lg">{weather.emoji}</span>
          <div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-sm font-medium text-[var(--text-primary)]">
                {weather.temperature}°C
              </span>
              {feelsLikeDiff && (
                <span className="text-xs text-[var(--text-secondary)]">
                  feels {weather.apparentTemperature}°C
                </span>
              )}
            </div>
            <p className="text-xs text-[var(--text-secondary)]">
              {weather.label}
              {windNotable && ` · Wind ${weather.windSpeed} km/h`}
              {weather.precipitation > 0 && ` · ${weather.precipitation} mm rain`}
            </p>
          </div>
        </div>
      </div>

      {onConsiderWeatherChange && (
        <div className="flex items-center justify-between pt-1 border-t border-[var(--border)]/50">
          <p className="text-xs text-[var(--text-secondary)]">Consider the weather</p>
          <button
            onClick={() => onConsiderWeatherChange(!considerWeather)}
            className={`relative w-9 h-5 rounded-full transition-colors ${
              considerWeather ? 'bg-[var(--accent-teal)]' : 'bg-[var(--bg-primary)] border border-[var(--border)]'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                considerWeather ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      )}
    </div>
  );
}
